import { ChangeEvent } from "react";

import { useTranslator } from "@eo-locale/react";

import { CommonSingleLineQuestion } from "@/storybook-components";

import { setSurveyAnswers } from "@/redux/features/survey/slice";
import type { Question } from "@/types";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { textStyles, spacing } from "./question-styles";
import { surveyParticipantFieldControl } from "./survey-participant-test-ids";
import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";

interface ISingleLineQuestion {
  question: Question;
  participantFieldId: string;
}

const SingleLineQuestion = ({
  question,
  participantFieldId
}: ISingleLineQuestion): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, saving } = survey;

  const error = errors[question.id] || {};
  const maxLength = question.maxLength || 255;
  const answer = answers[question.id]?.value ?? "";

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: { value: e.target.value }
      })
    );
  };

  return (
    <CommonSingleLineQuestion
      layoutProps={{ width: "100%" }}
      invalid={!!error.code}
      required={question.required}
      className={error.code ? "ehq-question-error" : null}
      gap={spacing.question}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelProps={{ ...textStyles?.label }}
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      answer={answer}
      questionId={question?.id}
      disabled={saving}
      changeEventHandler={handleChange}
      contentClassName="ehq-surveyInputFeild"
      size="lg"
      answerProps={{ ...textStyles?.content }}
      footerStackSpacing={spacing.footerStack}
      maxLength={maxLength}
      maxNote={translate("common.MaxLengthHelper", {
        count: maxLength
      })}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
      answerDataTestId={surveyParticipantFieldControl(
        participantFieldId,
        "single-line-input"
      )}
    />
  );
};

export default SingleLineQuestion;
